/**
 * Session Timeout Manager
 * Manages session timeout based on user inactivity
 * Tracks user activity and triggers session expiration after a configured idle period
 */

import { notifySessionExpired } from './sessionExpirationService';

export interface SessionTimeoutConfig {
  /** Timeout duration in minutes */
  timeoutMinutes: number;
  /** Enable/disable session timeout */
  enabled: boolean;
  /** Events to track for user activity */
  activityEvents?: string[];
}

class SessionTimeoutManager {
  private timeoutId: NodeJS.Timeout | null = null;
  private config: SessionTimeoutConfig;
  private activityHandler: (() => void) | null = null;
  private isActive: boolean = false;
  private hasShownTimeoutMessage: boolean = false;

  constructor() {
    // Default configuration
    this.config = {
      timeoutMinutes: 30,
      enabled: true,
      activityEvents: ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'],
    };
  }

  /**
   * Initialize the session timeout manager
   * @param config Configuration options
   */
  public initialize(config: Partial<SessionTimeoutConfig>): void {
    this.config = { ...this.config, ...config };
    
    
    if (this.config.enabled) {
      this.start();
    } else {
    }
  }

  /**
   * Start the session timeout monitoring
   */
  public start(): void {
    if (this.isActive) {
      return;
    }

    this.isActive = true;
    this.setupActivityListeners();
    this.resetTimeout();
  }

  /**
   * Stop the session timeout monitoring
   */
  public stop(): void {
    if (!this.isActive) {
      return;
    }

    this.isActive = false;
    this.clearTimeout();
    this.removeActivityListeners();
  }

  /**
   * Reset the timeout countdown
   */
  public resetTimeout(): void {
    if (!this.config.enabled || !this.isActive) {
      return;
    }

    // Clear the flag when user performs an action (becomes active again)
    if (this.hasShownTimeoutMessage) {
      this.hasShownTimeoutMessage = false;
    }

    this.clearTimeout();

    const timeoutMs = this.config.timeoutMinutes * 60 * 1000;
    const expiryTime = new Date(Date.now() + timeoutMs);
    
    this.timeoutId = setTimeout(() => {
      this.handleTimeout();
    }, timeoutMs);
  }

  /**
   * Get the current configuration
   */
  public getConfig(): SessionTimeoutConfig {
    return { ...this.config };
  }

  /**
   * Check if the manager is currently active
   */
  public isActiveState(): boolean {
    return this.isActive;
  }

  /**
   * Setup event listeners for user activity
   */
  private setupActivityListeners(): void {
    this.activityHandler = () => this.resetTimeout();

    const events = this.config.activityEvents || [];
    events.forEach((event) => {
      window.addEventListener(event, this.activityHandler!, { passive: true });
    });
  }

  /**
   * Remove activity event listeners
   */
  private removeActivityListeners(): void {
    if (!this.activityHandler) {
      return;
    }

    const events = this.config.activityEvents || [];
    events.forEach((event) => {
      window.removeEventListener(event, this.activityHandler!);
    });

    this.activityHandler = null;
  }

  /**
   * Clear the current timeout
   */
  private clearTimeout(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  /**
   * Handle session timeout
   */
  private handleTimeout(): void {
    // Only trigger if we haven't already shown the timeout message
    if (this.hasShownTimeoutMessage) {
      // Keep the timer going but don't notify again
      this.timeoutId = setTimeout(() => {
        this.handleTimeout();
      }, this.config.timeoutMinutes * 60 * 1000);
      return;
    }

    
    // Mark that we've shown the timeout message
    this.hasShownTimeoutMessage = true;
    
    // Notify all callbacks
    notifySessionExpired();
    
    // Keep monitoring for activity (resetTimeout will clear the flag when user becomes active)
    this.timeoutId = setTimeout(() => {
      this.handleTimeout();
    }, this.config.timeoutMinutes * 60 * 1000);
  }
}

// Export singleton instance
export const sessionTimeoutManager = new SessionTimeoutManager();
